import React from "react";
import { HOLIDAY_OFF, WEEK_OFF } from "../../Constant";
import { extractDate } from "../../Constant/common";
import { attendanceLabel } from "../../Constant/HR_Attendance/hrAttendance";
import { Clock, FileText, List, RotateCcw } from "react-feather";

/* ── Status colour map ─────────────────────────────────────────────────── */
const STATUS_STYLE = {
  [attendanceLabel.PresentStatus]: {
    color: "var(--status-success)",
    bg: "var(--status-success-bg)",
    label: "Present",
  },
  [attendanceLabel.HalfDayStatus]: {
    color: "var(--status-warning)",
    bg: "var(--status-warning-bg)",
    label: "Half Day",
  },
  [attendanceLabel.AbsentStatus]: {
    color: "var(--status-danger)",
    bg: "var(--status-danger-bg)",
    label: "Absent",
  },
  [attendanceLabel.CompOffStatus]: {
    color: "var(--status-purple)",
    bg: "var(--status-purple-bg)",
    label: "Comp Off",
  },
  [attendanceLabel.PaidLeaveStatus]: {
    color: "var(--brand-indigo)",
    bg: "var(--status-indigo-bg)",
    label: "Leave",
  },
  [attendanceLabel.OvertimeStatus]: {
    color: "#f97316",
    bg: "rgba(249,115,22,0.12)",
    label: "Overtime",
  },
  [attendanceLabel.LessHourStatus]: {
    color: "#ec4899",
    bg: "rgba(236, 72, 153, 0.12)",
    label: "Less Hour",
  },
  "Week Off": { color: "var(--brand-cyan)", bg: "var(--status-cyan-bg)", label: "Week Off" },
  Holiday: { color: "var(--status-orange)", bg: "var(--status-orange-bg)", label: "Holiday" },
  [attendanceLabel.noteMarked]: {
    color: "var(--text-secondary)",
    bg: "var(--status-neutral-bg)",
    label: "Not Marked",
  },
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#10b981,#06b6d4)",
  "linear-gradient(135deg,#8b5cf6,#ec4899)",
  "linear-gradient(135deg,#06b6d4,#6366f1)",
];

const StaffCard = ({
  title,
  subtitle,
  workingHours,
  historyEntries,
  status,
  note,
  onEditNote,
  onViewLogs,
  onStatusClick,
  statusLabels,
  shiftName,
  isHoliday,
  isWeekOff,
  RevertApi,
  RevertCompOff,
  staffAttendanceId,
  compOffHistory,
  isCompOff,
  actualStatus,
  leaveType,
  overtimeRequests = [],
  isOvertimeEligible = false,
  overtimeRequestedMinutes = null,
}) => {
  const sc = STATUS_STYLE[status] || STATUS_STYLE[attendanceLabel.noteMarked];
  const isNotMarked = status === attendanceLabel.noteMarked;
  const avatarGradient = AVATAR_GRADIENTS[(title?.charCodeAt(0) || 0) % AVATAR_GRADIENTS.length];

  const getDisplayStatus = () => {
    if (status === attendanceLabel.PaidLeaveStatus) return leaveType || "Leave";
    return status;
  };

  const getTimeDisplay = () => {
    if (!historyEntries?.inTime) return null;
    return `${historyEntries.inTime} → ${historyEntries.outTime ?? "—"}`;
  };

  const canClick = (labelStatus) => {
    const isWeekOffDay =
      status === WEEK_OFF ||
      isWeekOff ||
      actualStatus === WEEK_OFF ||
      actualStatus === attendanceLabel.WeekOff;
    const isHolidayDay =
      status === HOLIDAY_OFF ||
      isHoliday ||
      actualStatus === HOLIDAY_OFF ||
      actualStatus === "Holiday";

    if (isWeekOffDay || isHolidayDay) {
      return (
        labelStatus === attendanceLabel.PresentStatus ||
        labelStatus === attendanceLabel.HalfDayStatus
      );
    }
    return true;
  };

  return (
    <div
      style={{
        background: "var(--glass-bg)",
        border: `1px solid ${isNotMarked ? "rgba(244,63,94,0.25)" : "var(--glass-border)"}`,
        borderRadius: 16,
        overflow: "hidden",
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 32px rgba(99,102,241,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          height: 3,
          background: sc.bg,
          borderBottom: `1px solid ${sc.color}30`,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }}
      />

      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {/* ── Header row: avatar + info + status badge ── */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          {/* Avatar */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              flexShrink: 0,
              background: avatarGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.2)",
            }}
          >
            <span style={{ fontSize: "0.875rem", fontWeight: 800, color: "#fff" }}>
              {getInitials(title)}
            </span>
          </div>

          {/* Name + meta */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: "0.9375rem",
                color: "var(--text-primary)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title || "—"}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                fontFamily: "monospace",
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
            {shiftName && (
              <div
                style={{
                  fontSize: "0.6875rem",
                  color: "var(--text-muted)",
                  marginTop: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Clock size={10} strokeWidth={2} />
                {shiftName}
                {getTimeDisplay() && ` · ${getTimeDisplay()}`}
              </div>
            )}
          </div>

          {/* Status badge */}
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 4,
            }}
          >
            <span
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: "0.6875rem",
                fontWeight: 700,
                background: sc.bg,
                color: sc.color,
                whiteSpace: "nowrap",
              }}
            >
              {getDisplayStatus()}
            </span>
            {actualStatus &&
              (actualStatus === attendanceLabel.Holiday ||
                actualStatus === attendanceLabel.WeekOff) &&
              status !== WEEK_OFF &&
              status !== HOLIDAY_OFF && (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 999,
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    background: "rgba(249,115,22,0.1)",
                    color: "#f97316",
                  }}
                >
                  {actualStatus}
                </span>
              )}
            {workingHours && (
              <span style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>
                {workingHours}
              </span>
            )}
          </div>
        </div>

        {/* ── Status action buttons ── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {statusLabels?.map((label, i) => {
            const hasOvertime =
              label.status === attendanceLabel.OvertimeStatus &&
              (overtimeRequestedMinutes != null ||
                (overtimeRequests && overtimeRequests.length > 0));
            const isActive = status === label.status || hasOvertime;
            const lsc = STATUS_STYLE[label.status] || {
              color: "#64748b",
              bg: "rgba(100,116,139,0.1)",
            };
            const isOvertimeLabel = label.status === attendanceLabel.OvertimeStatus;
            let clickable = hasOvertime ? true : canClick(label.status);
            if (isOvertimeLabel && !hasOvertime) {
              clickable = status === attendanceLabel.PresentStatus && isOvertimeEligible;
            }
            return (
              <button
                key={i}
                onClick={() => clickable && onStatusClick(label.status)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 8,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  border: isActive ? `1.5px solid ${lsc.color}` : "1px solid var(--glass-border)",
                  background: isActive ? lsc.bg : "var(--icon-btn-bg)",
                  color: isActive ? lsc.color : "var(--text-secondary)",
                  cursor: clickable ? "pointer" : "default",
                  opacity: clickable ? 1 : 0.5,
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  if (clickable && !isActive) {
                    e.currentTarget.style.background = lsc.bg;
                    e.currentTarget.style.color = lsc.color;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "var(--icon-btn-bg)";
                    e.currentTarget.style.color = "var(--text-secondary)";
                  }
                }}
              >
                {label.displayText}
              </button>
            );
          })}
        </div>

        {/* ── Footer: note + comp off + actions ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            paddingTop: 8,
            borderTop: "1px solid var(--glass-border)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 }}>
            {compOffHistory && (
              <span style={{ fontSize: "0.6875rem", color: "#8b5cf6" }}>
                {attendanceLabel.CompOffDate}
                {extractDate(compOffHistory)}
              </span>
            )}
            {note && (
              <span
                style={{
                  fontSize: "0.6875rem",
                  color: "var(--text-muted)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 200,
                }}
                title={note}
              >
                {note}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {/* Revert buttons */}
            {(isHoliday || actualStatus === HOLIDAY_OFF || actualStatus === "Holiday") &&
              status !== HOLIDAY_OFF &&
              status !== attendanceLabel.CompOffStatus &&
              !compOffHistory &&
              isCompOff !== true && (
                <button
                  onClick={() => RevertApi(staffAttendanceId)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 10px",
                    borderRadius: 7,
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    background: "rgba(244,63,94,0.1)",
                    border: "1px solid rgba(244,63,94,0.2)",
                    color: "#f43f5e",
                    cursor: "pointer",
                  }}
                >
                  <RotateCcw size={11} strokeWidth={2.5} />
                  {attendanceLabel.Revert}
                </button>
              )}
            {(isWeekOff || actualStatus === WEEK_OFF || actualStatus === "Week Off") &&
              status !== WEEK_OFF &&
              status !== attendanceLabel.CompOffStatus &&
              !compOffHistory &&
              isCompOff !== true && (
                <button
                  onClick={() => RevertCompOff(staffAttendanceId)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 10px",
                    borderRadius: 7,
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    background: "rgba(244,63,94,0.1)",
                    border: "1px solid rgba(244,63,94,0.2)",
                    color: "#f43f5e",
                    cursor: "pointer",
                  }}
                >
                  <RotateCcw size={11} strokeWidth={2.5} />
                  {attendanceLabel.RevertWeekOff}
                </button>
              )}

            {/* Note */}
            <button
              onClick={onEditNote}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                borderRadius: 7,
                fontSize: "0.6875rem",
                fontWeight: 600,
                background: "var(--icon-btn-bg)",
                border: "1px solid var(--glass-border)",
                color: "var(--text-secondary)",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--brand-indigo)";
                e.currentTarget.style.borderColor = "rgba(99,102,241,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--text-secondary)";
                e.currentTarget.style.borderColor = "var(--glass-border)";
              }}
            >
              <FileText size={11} strokeWidth={2} />
              {note ? attendanceLabel.editNote : attendanceLabel.AddNote}
            </button>

            {/* Logs */}
            <button
              onClick={onViewLogs}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                borderRadius: 7,
                fontSize: "0.6875rem",
                fontWeight: 600,
                background: "var(--icon-btn-bg)",
                border: "1px solid var(--glass-border)",
                color: "var(--text-secondary)",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--brand-indigo)";
                e.currentTarget.style.borderColor = "rgba(99,102,241,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--text-secondary)";
                e.currentTarget.style.borderColor = "var(--glass-border)";
              }}
            >
              <List size={11} strokeWidth={2} />
              {attendanceLabel.ViewLogs}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffCard;
